import { __awaiter } from "tslib";
import qs from 'qs';
import { parseGetSitesResponse } from '@/search-for-retail/site/site.parsers';
import { ccApi } from './ccApi';
import { CC_API_TAGS } from './constants';
/** Path segment for the site filter resource under a collection. */
const SITE_FILTER_PATH = 'siteFilter';
// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------
/**
 * RTK Query endpoints for site filter data.
 *
 * Site filter endpoints sit under `/collections/{collection}/siteFilter`.
 * Auth and tenant headers are handled by the base `ccApi`.
 *
 * @see https://redux-toolkit.js.org/rtk-query/api/created-api/code-splitting
 */
export const siteApi = ccApi.injectEndpoints({
    endpoints: (builder) => ({
        // -----------------------------------------------------------------------
        // Site queries
        // -----------------------------------------------------------------------
        /**
         * Fetches the short list of sites for a collection.
         *
         * @endpoint GET /collections/{collection}/siteFilter/short
         */
        getSiteShortList: builder.query({
            query: ({ collection }) => `/collections/${collection}/${SITE_FILTER_PATH}/short`,
            providesTags: [CC_API_TAGS.SiteShortList],
        }),
        /**
         * Fetches a paginated list of sites for a collection.
         *
         * @endpoint GET /collections/{collection}/siteFilter?page=&size=&sort=&order=&name=
         */
        getSiteList: builder.query({
            query: ({ collection, page, perPage, sort, filter, }) => {
                const { propertyName, direction } = sort !== null && sort !== void 0 ? sort : {};
                const queryParams = qs.stringify({
                    page,
                    size: perPage,
                    sort: propertyName,
                    order: direction,
                    name: filter === null || filter === void 0 ? void 0 : filter.name,
                });
                return `/collections/${collection}/${SITE_FILTER_PATH}?${queryParams}`;
            },
            transformResponse: (response) => parseGetSitesResponse(response),
            providesTags: [CC_API_TAGS.SiteList],
        }),
        /**
         * Fetches a single site by ID.
         *
         * @endpoint GET /collections/{collection}/siteFilter/{id}
         */
        getSiteById: builder.query({
            query: ({ collection, id }) => `/collections/${collection}/${SITE_FILTER_PATH}/${id}`,
            providesTags: (_result, _error, { id }) => [
                { type: CC_API_TAGS.Site, id },
            ],
        }),
        /**
         * Looks up a site filter ID by site filter name.
         *
         * @endpoint GET /collections/{collection}/siteFilter/name/{name}
         */
        getSiteFilterIdByName: builder.query({
            query: ({ collection, name }) => `/collections/${collection}/${SITE_FILTER_PATH}/name/${name}`,
            transformResponse: (response) => response === null || response === void 0 ? void 0 : response.id,
        }),
        // -----------------------------------------------------------------------
        // Site mutations
        // -----------------------------------------------------------------------
        /**
         * Creates a new site in a collection.
         *
         * Extracts the `x-groupby-location-page` response header
         * so consumers can highlight newly created models.
         *
         * @endpoint POST /collections/{collection}/siteFilter
         */
        createSite: builder.mutation({
            queryFn(_a, _queryApi_1, _extraOptions_1, baseQuery_1) {
                return __awaiter(this, arguments, void 0, function* ({ collection, data }, _queryApi, _extraOptions, baseQuery) {
                    var _b, _c, _d;
                    const result = yield baseQuery({
                        url: `/collections/${collection}/${SITE_FILTER_PATH}`,
                        method: 'POST',
                        body: data,
                    });
                    if (result.error) {
                        return { error: result.error };
                    }
                    const site = result.data;
                    const meta = result.meta;
                    const locationPage = (_d = (_c = (_b = meta === null || meta === void 0 ? void 0 : meta.response) === null || _b === void 0 ? void 0 : _b.headers) === null || _c === void 0 ? void 0 : _c.get('x-groupby-location-page')) !== null && _d !== void 0 ? _d : null;
                    return { data: { site, locationPage } };
                });
            },
            invalidatesTags: [CC_API_TAGS.SiteList, CC_API_TAGS.SiteShortList],
        }),
        /**
         * Updates an existing site.
         *
         * @endpoint PUT /collections/{collection}/siteFilter/{id}
         */
        updateSite: builder.mutation({
            query: ({ collection, data }) => ({
                url: `/collections/${collection}/${SITE_FILTER_PATH}/${data.id}`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: (_result, _error, { data }) => [
                CC_API_TAGS.SiteList,
                CC_API_TAGS.SiteShortList,
                { type: CC_API_TAGS.Site, id: data.id },
            ],
        }),
        /**
         * Deletes a single site.
         *
         * @endpoint DELETE /collections/{collection}/siteFilter/{id}
         */
        deleteSite: builder.mutation({
            query: ({ collection, id }) => ({
                url: `/collections/${collection}/${SITE_FILTER_PATH}/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, { id }) => [
                CC_API_TAGS.SiteList,
                CC_API_TAGS.SiteShortList,
                { type: CC_API_TAGS.Site, id },
            ],
        }),
    }),
});
export const { useCreateSiteMutation, useDeleteSiteMutation, useGetSiteByIdQuery, useLazyGetSiteFilterIdByNameQuery, useGetSiteListQuery, useGetSiteShortListQuery, useUpdateSiteMutation, } = siteApi;
